import {
  KeyboardEvent,
  ReactNode,
  useId,
  useState,
} from 'react';

export interface TabItem {
  value: string;
  label: ReactNode;
  content?: ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  label: string;
  items: TabItem[];
  value?: string;
  defaultValue?: string;
  variant?: 'underline' | 'pills';
  size?: 'default' | 'small';
  plainPanels?: boolean;
  className?: string;
  onValueChange?: (value: string) => void;
}

const Tabs = ({
  label,
  items,
  value,
  defaultValue,
  variant = 'underline',
  size = 'default',
  plainPanels = false,
  className = '',
  onValueChange,
}: TabsProps) => {
  const id = useId();
  const isControlled = value !== undefined;
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const [focused, setFocused] = useState<string>();
  const currentValue = isControlled ? value : uncontrolledValue;

  const active =
    items.find((item) => item.value === currentValue && !item.disabled)?.value ??
    items.find((item) => !item.disabled)?.value;

  const focusValue =
    items.find((item) => item.value === focused && !item.disabled)?.value ?? active;

  const hasPanels = items.some((item) => item.content !== undefined);

  const selectValue = (next: string) => {
    if (!isControlled) {
      setUncontrolledValue(next);
    }
    setFocused(next);
    onValueChange?.(next);
  };

  const keydown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const list = event.currentTarget;
    const buttons = Array.from(
      list.querySelectorAll<HTMLButtonElement>('[role="tab"]:not(:disabled)')
    );
    const index = buttons.indexOf(event.target as HTMLButtonElement);
    if (index < 0) return;
    const rtl =
      getComputedStyle(list).direction === 'rtl' ||
      list.closest('[dir]')?.getAttribute('dir') === 'rtl';
    let next: number;
    switch (event.key) {
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = buttons.length - 1;
        break;
      case 'ArrowRight':
        next = index + (rtl ? -1 : 1);
        break;
      case 'ArrowLeft':
        next = index + (rtl ? 1 : -1);
        break;
      default:
        return;
    }
    event.preventDefault();
    const target = buttons[(next + buttons.length) % buttons.length];
    if (!target) return;
    setFocused(target.dataset.value);
    target.focus();
  };

  return (
    <div
      className={[
        'kit-selection',
        'kit-tabs',
        variant === 'pills' ? 'kit-tabs--pills' : '',
        size === 'small' ? 'kit-tabs--small' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="kit-tablist" role="tablist" aria-label={label} onKeyDown={keydown}>
        {items.map((item, index) => (
          <button
            type="button"
            className="kit-tab"
            role="tab"
            key={item.value}
            id={`${id}-tab-${index}`}
            aria-controls={hasPanels ? `${id}-panel-${index}` : undefined}
            aria-selected={item.value === active}
            disabled={item.disabled}
            tabIndex={item.value === focusValue ? 0 : -1}
            data-value={item.value}
            onClick={() => selectValue(item.value)}
          >
            {item.label}
          </button>
        ))}
      </div>
      {hasPanels &&
        items.map((item, index) => (
          <div
            className={
              'kit-tabpanel' + (plainPanels ? ' kit-tabpanel-plain' : '')
            }
            role="tabpanel"
            key={item.value}
            id={`${id}-panel-${index}`}
            aria-labelledby={`${id}-tab-${index}`}
            tabIndex={0}
            hidden={item.value !== active}
          >
            {item.content}
          </div>
        ))}
    </div>
  );
};

export default Tabs;
